import React, { useEffect, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth } from "../../contexts/AuthContext";
import { Printer, MapPin, ShieldCheck, AlertTriangle } from "lucide-react";

const TYPES = ["", "doctor", "distributor", "retailer", "patient", "other"];

export default function MRDues() {
    const { user } = useAuth();
    const [type, setType] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [data, setData] = useState({
        rows: [],
        totals: { billed: 0, paid: 0, outstanding: 0 },
        territory: "",
        scope: "",
    });

    const load = async () => {
        const params = {};
        if (type) params.customer_type = type;
        if (from) params.from_date = from;
        if (to) params.to_date = to;
        const { data } = await api.get("/mr/dues", { params });
        setData({
            rows: data.rows || [],
            totals: data.totals || { billed: data.total_billed || 0, paid: data.total_paid || 0, outstanding: data.total_outstanding || 0 },
            territory: data.territory || "",
            scope: data.scope || "",
        });
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const isMrNoTerritory = data.scope === "mr_no_territory";
    const isMrTerritory = data.scope === "mr_territory";
    const isUnrestricted = data.scope === "unrestricted";

    return (
        <div data-testid="mr-dues" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow text-sky-700">Field intel</div>
                    <h1 className="font-serif text-4xl text-stone-900">Customer dues</h1>
                </div>
                <Button onClick={() => window.print()} variant="outline" className="rounded-none border-stone-400 print:hidden" data-testid="mr-dues-print">
                    <Printer className="w-4 h-4 mr-2" />Print
                </Button>
            </div>

            {/* Territory / scope banner — tells the MR exactly what they're seeing. */}
            {isMrTerritory && (
                <div className="bg-sky-50 border-l-4 border-sky-700 px-4 py-3 flex items-center gap-3" data-testid="mr-dues-territory-banner">
                    <MapPin className="w-4 h-4 text-sky-700 shrink-0" />
                    <div className="text-sm text-sky-900">
                        Showing customers from your assigned territory: <strong>{data.territory}</strong>
                        <div className="text-xs text-sky-700 mt-0.5">Other states are hidden by company policy. Contact admin to update.</div>
                    </div>
                </div>
            )}
            {isUnrestricted && (
                <div className="bg-emerald-50 border-l-4 border-emerald-600 px-4 py-3 flex items-center gap-3" data-testid="mr-dues-unrestricted-banner">
                    <ShieldCheck className="w-4 h-4 text-emerald-700 shrink-0" />
                    <div className="text-sm text-emerald-900">
                        Showing <strong>all customer dues</strong> (admin / editor / Accounts & Billing access).
                    </div>
                </div>
            )}
            {isMrNoTerritory && (
                <div className="bg-amber-50 border-l-4 border-amber-600 px-4 py-3 flex items-start gap-3" data-testid="mr-dues-no-territory-banner">
                    <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900">
                        <div className="font-medium">No territory assigned yet.</div>
                        <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                            Customer dues are filtered by the states you cover. Ask your admin to open <em>Admin → Users → Edit you → Territory</em> and add the states you're responsible for (e.g. "Assam" or "Assam, Meghalaya"). Until then this page will stay empty.
                        </p>
                    </div>
                </div>
            )}

            <div className="bg-white border border-stone-200 p-4 grid sm:grid-cols-4 gap-3 print:hidden">
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Type</Label>
                    <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
                        <SelectTrigger className="rounded-none border-stone-300 mt-1"><SelectValue placeholder="All" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            {TYPES.filter(Boolean).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">From</Label>
                    <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none border-stone-300 mt-1" />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">To</Label>
                    <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none border-stone-300 mt-1" />
                </div>
                <div className="flex items-end">
                    <Button onClick={load} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50">Apply</Button>
                </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
                <KPI label="Billed" value={formatINR(data.totals.billed)} />
                <KPI label="Paid" value={formatINR(data.totals.paid)} />
                <KPI label="Outstanding" value={formatINR(data.totals.outstanding)} highlight />
            </div>

            <div className="bg-white border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-stone-200 bg-stone-50">
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Customer</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Type</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Phone</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">City</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">State</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Billed</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Paid</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Outstanding</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.length === 0 ? (
                            <tr><td colSpan={8} className="p-8 text-center text-stone-500">
                                {isMrNoTerritory ? "Ask admin to assign you a territory to see dues here." : "No dues in this filter."}
                            </td></tr>
                        ) : data.rows.map((r, i) => (
                            <tr key={i} className="border-b border-stone-100" data-testid={`dues-row-${i}`}>
                                <td className="p-3 font-medium">{r.name}</td>
                                <td className="p-3 text-xs uppercase tracking-widest text-stone-500">{r.customer_type}</td>
                                <td className="p-3 text-stone-500">{r.phone}</td>
                                <td className="p-3 text-stone-500">{r.city}</td>
                                <td className="p-3 text-stone-700">{r.state || "—"}</td>
                                <td className="p-3 text-right">{formatINR(r.billed)}</td>
                                <td className="p-3 text-right">{formatINR(r.paid)}</td>
                                <td className="p-3 text-right font-medium text-amber-700">{formatINR(r.outstanding)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const KPI = ({ label, value, highlight }) => (
    <div className={`bg-white border ${highlight ? "border-amber-500" : "border-stone-200"} p-4`}>
        <div className="label-eyebrow text-stone-500">{label}</div>
        <div className={`font-serif text-2xl mt-1 ${highlight ? "text-amber-700" : "text-stone-900"}`}>{value}</div>
    </div>
);
